import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { usePushHistories } from '../../hooks/usePush';
import { PushTargetType, PushTargetTypeLabels } from '../../types/push';
import SendPushDialog from './SendPushDialog';
import { formatKstDateTime } from '../../utils/dateTime';

export default function PushList() {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [dialogOpen, setDialogOpen] = useState(false);

    const { data, isLoading, error } = usePushHistories(page, rowsPerPage);

    const handlePageChange = (_: unknown, newPage: number) => {
        setPage(newPage + 1);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const getTargetTypeColor = (type: PushTargetType): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
        switch (type) {
            case PushTargetType.ALL:
                return 'primary';
            case PushTargetType.SUBSCRIBER:
                return 'success';
            case PushTargetType.FREE:
                return 'info';
            case PushTargetType.ACTIVE_7D:
                return 'warning';
            case PushTargetType.INACTIVE_7D:
                return 'error';
            case PushTargetType.INDIVIDUAL:
                return 'secondary';
            default:
                return 'default';
        }
    };

    if (error) {
        return <Alert severity="error">발송 이력을 불러오는데 실패했습니다.</Alert>;
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">푸시 발송 관리</Typography>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setDialogOpen(true)}
                >
                    그룹 푸시 발송
                </Button>
            </Box>

            <Paper>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>발송 대상</TableCell>
                                        <TableCell>제목</TableCell>
                                        <TableCell>내용</TableCell>
                                        <TableCell align="right">대상 수</TableCell>
                                        <TableCell align="right">성공</TableCell>
                                        <TableCell align="right">실패</TableCell>
                                        <TableCell>발송자</TableCell>
                                        <TableCell>발송일시</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data?.content ?? []).map((history) => (
                                        <TableRow key={history.id}>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        history.targetType === PushTargetType.INDIVIDUAL
                                                            ? history.targetUserEmail || '개별'
                                                            : PushTargetTypeLabels[history.targetType]
                                                    }
                                                    size="small"
                                                    color={getTargetTypeColor(history.targetType)}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {history.title}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {history.body}
                                            </TableCell>
                                            <TableCell align="right">{history.totalTargetCount}</TableCell>
                                            <TableCell align="right">
                                                <Typography color="success.main">{history.successCount}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography color={history.failureCount > 0 ? 'error.main' : 'text.secondary'}>
                                                    {history.failureCount}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{history.sentByEmail}</TableCell>
                                            <TableCell>
                                                {formatKstDateTime(history.sentAt)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(!data || (data.content ?? []).length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                <Typography color="text.secondary">
                                                    발송 이력이 없습니다.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {data && (
                            <TablePagination
                                component="div"
                                count={data.totalCount ?? 0}
                                page={page - 1}
                                onPageChange={handlePageChange}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPageOptions={[10, 20, 50]}
                                labelRowsPerPage="페이지당 행 수"
                            />
                        )}
                    </>
                )}
            </Paper>

            <SendPushDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                mode="group"
            />
        </Box>
    );
}
